import { isStartingInNext24Hours } from '$tablet-app/src/app/calendar/common/actions';
import BackgroundTimer from '$tablet-app/src/app/timer/BackgroundTimer';
import { useListAllAlarmsByHabitatQuery } from '@akin-home/alarms/react';
import { useListAllAlertsByHabitatQuery } from '@akin-home/alerts/react';
import { MealType } from '@akin-home/amplify';
import { useListEventsByHabitatQuery } from '@akin-home/events/react';
import { useListMealsByHabitat } from '@akin-home/meals/react';
import { findBooleanStateVariableByName } from '@akin-home/state-variables';
import { addDays, startOfDay } from 'date-fns';
import { useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { CalendarItemType } from '../models';
import { useStateStore } from '../stores/useStateStore';

let queryTimer: any;

const getQueryParams = () => ({
  from: startOfDay(new Date()).toISOString(),
  to: startOfDay(addDays(new Date(), 1)).toISOString(),
});

type Params = {
  habitatID: string;
};

// This hook sets up the timers for calendar alerts to be displayed at their specified times
export default function useReminderNotifications(params: Params) {
  const { habitatID } = params;

  const [filter, setQueryParams] = useState<any>(getQueryParams());

  const query_params = {
    habitatID,
    filter,
  };

  const { data: alarms } = useListAllAlarmsByHabitatQuery(query_params);
  const { data: alerts } = useListAllAlertsByHabitatQuery(query_params);
  const { data: events } = useListEventsByHabitatQuery(query_params);
  const { data: meals } = useListMealsByHabitat({
    habitatID,
    start: { between: [filter.from, filter.to] },
  });

  const getStateVariables = useStateStore((state) => state.getStateVariables);
  const setUpcomingCalendarItems = useStateStore(
    (state) => state.setUpcomingCalendarItems
  );
  const [startReminderListener, stopReminderListener] = useStateStore(
    (state) => [state.startReminderListener, state.stopReminderListener],
    shallow
  );

  useEffect(() => {
    setUpcomingCalendarItems(
      CalendarItemType.Alarm,
      alarms?.filter(isStartingInNext24Hours)
    );
  }, [alarms, setUpcomingCalendarItems]);

  useEffect(() => {
    setUpcomingCalendarItems(
      CalendarItemType.Alert,
      alerts?.filter(isStartingInNext24Hours)
    );
  }, [alerts, setUpcomingCalendarItems]);

  useEffect(() => {
    setUpcomingCalendarItems(
      CalendarItemType.Event,
      events?.filter(isStartingInNext24Hours)
    );
  }, [events, setUpcomingCalendarItems]);

  useEffect(() => {
    getStateVariables([
      'User_breakfast_reminder',
      'User_lunch_reminder',
      'User_dinner_reminder',
      'User_snack_reminder',
    ]).then((variables) => {
      if (!variables?.length) return;

      const reminders = {
        [MealType.Breakfast]: findBooleanStateVariableByName(
          variables,
          'User_breakfast_reminder'
        )?.value,

        [MealType.Lunch]: findBooleanStateVariableByName(
          variables,
          'User_lunch_reminder'
        )?.value,

        [MealType.Dinner]: findBooleanStateVariableByName(
          variables,
          'User_dinner_reminder'
        )?.value,

        [MealType.Snack]: findBooleanStateVariableByName(
          variables,
          'User_snack_reminder'
        )?.value,
      };

      setUpcomingCalendarItems(
        CalendarItemType.Meal,
        meals
          ?.filter(isStartingInNext24Hours)
          ?.filter(({ mealType }) => reminders[mealType])
      );
    });
  }, [meals, setUpcomingCalendarItems, getStateVariables]);

  useEffect(() => {
    startReminderListener();

    if (queryTimer) {
      BackgroundTimer.clearInterval(queryTimer);
    }

    queryTimer = BackgroundTimer.setInterval(() => {
      setQueryParams(getQueryParams());
    }, 1000 * 60 * 60);

    return () => {
      if (queryTimer) {
        BackgroundTimer.clearInterval(queryTimer);
      }
      stopReminderListener();
    };
  }, [startReminderListener, stopReminderListener]);
}
